/**
 * №29 (S1) — the work rail's CONTEXT tab. Mockup A's "BOUND CONTEXT" and "STANDING
 * INSTRUCTION": the records this piece of work is attached to, and the wall it runs
 * behind.
 *
 * This is the tab the probe's caption is about:
 *
 *   *"What's ours and can't be copied: every context chip is a live record — a trust
 *   balance, a WIP figure, a clause in the engagement letter."*
 *
 * So every chip below is read from a record that exists, through the shipped readers
 * (`get_row_detail`, `get_matter_trust_balance`, `get_wip_for_matter`, and the №11.2
 * capability derivation). A figure that has not loaded renders as a figure that has
 * not loaded; a field the record does not carry renders as nothing at all. Neither is
 * dressed as a value — the em-dash contract cuts both ways, and a rail full of
 * plausible chips would be the single most damaging thing this stage could ship.
 *
 * The STANDING INSTRUCTION chip is the money wall, derived from the same records the
 * hire's desk derives it from (`deriveCapabilities` over role assignments · roles ·
 * areas — R1 reuse of the pure function, not a second policy read). It is shown only
 * when a named member of staff is actually carrying this work, because otherwise
 * there is no one for the instruction to bind.
 *
 * D-L6: a member of staff, a hire, a named role. Never "the agent".
 */
import { Link } from "react-router";
import { useRowDetail } from "../../../hooks/use-row-detail";
import { useEntityNameResolver } from "../../../hooks/use-entity-names";
import { useMatterTrust, useMatterWip } from "../../../hooks/use-time-billing";
import { useAreas, useRoleAssignments, useRoles } from "../../../hooks/use-policy";
import { useLibraryNode } from "../../../../lib/hooks/use-kuvio";
import { money } from "../../ledger/format";
import { deriveCapabilities } from "../../../routes/app/staff-desk";
import { RailChip, RailEmpty, RailHead, RailLoading } from "./RailPrimitives";
import type { RailBinding } from "./rail-binding";
import type { AgentConfig } from "../../../../lib/types";

function fmtDate(iso: string): string {
  try {
    return new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso).toLocaleDateString("en-AU", { day: "numeric", month: "short" });
  } catch {
    return iso;
  }
}

function daysUntil(iso: string): number | null {
  try {
    return Math.round((new Date(iso.length <= 10 ? `${iso}T00:00:00Z` : iso).getTime() - Date.now()) / 86_400_000);
  } catch {
    return null;
  }
}

/**
 * The soonest upcoming date-valued field on the record — the same derivation the
 * task cockpit's context rail uses (№22.1 D-CC1), so "the key date" means one thing
 * in both places. It is a READ of the record's own fields, never a stored opinion.
 */
function pickKeyDate(entity: Record<string, unknown> | undefined): { label: string; iso: string } | null {
  if (!entity) return null;
  let best: { label: string; iso: string; days: number } | null = null;
  for (const [k, v] of Object.entries(entity)) {
    if (typeof v !== "string" || !/date|settle|due|expir|complet/i.test(k)) continue;
    if (!/^\d{4}-\d{2}-\d{2}/.test(v)) continue;
    const days = daysUntil(v);
    if (days == null || days < -3) continue;
    if (!best || days < best.days) {
      best = { label: k.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase()).replace(/ Date$/i, ""), iso: v, days };
    }
  }
  return best ? { label: best.label, iso: best.iso } : null;
}

/** The wall chip — shown only when a named hire is carrying the work. */
function StandingInstruction({ hire }: { hire: { id: string; name: string } }) {
  const node = useLibraryNode(hire.id);
  const assignments = useRoleAssignments();
  const roles = useRoles();
  const areas = useAreas();
  const caps = deriveCapabilities(hire.id, assignments.data ?? [], roles.data ?? [], areas.data);
  const cfg = (node.data?.agent_config ?? null) as AgentConfig | null;
  const autonomy = cfg?.autonomy ?? "off";
  const dial =
    autonomy === "autonomous"
      ? "Runs on its own — every run still leaves a thread"
      : autonomy === "supervised"
        ? "Runs when you press ▶ and watch"
        : "Drafts only — every run parks for you";

  return (
    <>
      <RailHead>Standing instruction</RailHead>
      <RailChip
        tone="warn"
        label={<>Cannot {caps.wallHead}</>}
        value="wall"
        title={caps.wallReason}
        testId="rail-context-wall"
      />
      <RailChip label={dial} value={autonomy} testId="rail-context-autonomy" />
    </>
  );
}

export function RailContext({ binding }: { binding: RailBinding }) {
  const { record, hire, task } = binding;
  const detail = useRowDetail(record?.type || undefined, record?.id);
  const isMatter = record?.type === "matter";
  const trust = useMatterTrust(isMatter ? record?.id : undefined);
  const wip = useMatterWip(isMatter ? record?.id : undefined);
  // The matter's `client` field holds a CONTACT ID. Rendering it raw would put
  // "C-0002" on a user surface under the word "client" — the same failure class as
  // S0's defect 1 (the matrix row's review label leaking into the stage head). So it
  // is resolved to the contact's own display name, and the chip is simply absent when
  // it cannot be (never the id as a consolation prize).
  //
  // Declared HERE, above the early returns, and not beside its use: it is a hook, and
  // a hook after a conditional return is the defect that crashed the whole panel in
  // 22.3.1 (P3). `npx eslint` is the only thing that sees it — tsc and vite do not.
  const resolveContact = useEntityNameResolver(["contact"]);

  if (!record && !hire) {
    return (
      <RailEmpty>
        Nothing is bound here. <b>{binding.label}</b> is the whole workspace — open a record, or
        click a run in the conversation, and what it is attached to appears here.
      </RailEmpty>
    );
  }
  if (record && detail.isLoading) return <RailLoading />;

  const entity = detail.data?.entity as Record<string, unknown> | undefined;
  const clientRef = (entity?.client as string) || (entity?.client_name as string) || "";
  const client = clientRef ? (resolveContact(clientRef) ?? "") : "";
  const practice = (entity?.practice_area as string) || (entity?.matter_type as string) || "";
  const responsible = (entity?.responsible_lawyer as string) || (entity?.responsible as string) || "";
  const keyDate = record ? pickKeyDate(entity) : null;
  const kd = keyDate ? daysUntil(keyDate.iso) : null;

  return (
    <div className="railpane" data-testid="rail-context">
      {record && (
        <>
          <RailHead>Bound context</RailHead>
          <Link className="railchiplink" to={`/app/place/${record.type}/${record.id}`}>
            <RailChip label={record.name} value={record.type} testId="rail-context-record" />
          </Link>
          {client && <RailChip label={client} value="client" testId="rail-context-client" />}
          {practice && <RailChip label={practice} value="type" />}
          {responsible && <RailChip label={responsible} value="supervising" testId="rail-context-supervising" />}
          {isMatter && (
            <>
              <RailChip
                tone="money"
                label="Trust balance"
                value={trust.data ? money(trust.data.balance_minor) : "…"}
                testId="rail-context-trust"
              />
              <RailChip
                tone="money"
                label="Unbilled WIP"
                value={wip.data ? money(wip.data.wip_minor) : "…"}
                testId="rail-context-wip"
              />
            </>
          )}
          {keyDate && (
            <RailChip
              tone="warn"
              label={keyDate.label}
              value={`${fmtDate(keyDate.iso)}${kd != null && kd >= 0 ? ` · ${kd}d` : ""}`}
              testId="rail-context-keydate"
            />
          )}
        </>
      )}

      {task && (
        <>
          <RailHead>This run is on</RailHead>
          <RailChip label={task.title} value="task" testId="rail-context-task" />
        </>
      )}

      {hire && <StandingInstruction hire={hire} />}
    </div>
  );
}
